/**
 * NegotiationsTab — orchestrator tab for the Vendor Negotiator feature.
 *
 * Composes:
 *  - Primary tab "Vendors": unified VendorsTable (all vendors) with campaign filter,
 *    "Create negotiation campaign" button, and SignalConfigDialog gear
 *  - Primary tab "Active Negotiations": Threads | Templates sub-nav
 *  - NewNegotiationDialog (prep stages 3→5 wired to buildNegotiationCase + suggestTerms)
 *  - DemoControls (gated)
 *
 * The tab is rendered only when the `vendor_negotiator` feature flag is enabled
 * (gating happens in PARSystemsPage; this component doesn't re-check the flag).
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquarePlus, MessageSquare, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { useNegotiations } from "@/hooks/useNegotiations";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { useVendorTermsLookup } from "@/hooks/useVendorTermsLookup";
import { useSettings } from "@/hooks/useSettings";
import { useNegotiationCampaigns } from "@/hooks/useNegotiationCampaigns";
import { useVendorLabels } from "@/hooks/useVendorLabels";
import { ThreadList } from "@/components/negotiations/ThreadList";
import { ThreadView } from "@/components/negotiations/ThreadView";
import { NewNegotiationDialog } from "@/components/negotiations/NewNegotiationDialog";
import { VendorsTable } from "@/components/negotiations/VendorsTable";
import { TemplatesManager } from "@/components/negotiations/TemplatesManager";
import { DemoControls } from "@/components/negotiations/DemoControls";
import { buildNegotiationSuggestions } from "@/lib/par/negotiationSuggestions";
import { buildVendorRoster } from "@/lib/par/vendorRoster";
import { buildNegotiationCase } from "@/lib/par/negotiationCase";
import { suggestTerms } from "@/lib/negotiation/suggestTerms";
import { CooldownError } from "@/hooks/useNegotiations";
import { useNegotiationConfig } from "@/hooks/useNegotiationConfig";
import { SignalConfigDialog } from "@/components/negotiations/SignalConfigDialog";
import type { PARInvoiceRow, VendorSummary } from "@/lib/par/csvInvoiceParser";
import type { NewNegotiationSubmitPayload } from "@/components/negotiations/NewNegotiationDialog";
import type { NegotiationCase } from "@/lib/par/negotiationCase";
import type { SuggestedTerms } from "@/lib/negotiation/suggestTerms";

// ---------------------------------------------------------------------------
// Loading skeleton (table-shaped)
// ---------------------------------------------------------------------------

function SuggestionsSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading vendors">
      <Skeleton className="h-8 w-48" />
      <Card className="overflow-hidden divide-y divide-border">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Skeleton className="h-7 w-7 rounded-full flex-shrink-0" />
            <Skeleton className="h-4 w-40" />
            <div className="flex gap-1.5 flex-1">
              <Skeleton className="h-4 w-16 rounded-full" />
              <Skeleton className="h-4 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-7 w-16 rounded-md" />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primary tab ids
// ---------------------------------------------------------------------------

type PrimaryTab = "vendors" | "active";

// ---------------------------------------------------------------------------
// Sub-panel ids within Active Negotiations
// ---------------------------------------------------------------------------

type SubPanel = "threads" | "templates";

/** Set to true to re-expose the Templates sub-tab under Active Negotiations. */
const SHOW_TEMPLATES = false;

const SUB_PANELS: { id: SubPanel; label: string }[] = [
  { id: "threads", label: "Threads" },
  ...(SHOW_TEMPLATES ? [{ id: "templates" as SubPanel, label: "Templates" }] : []),
];

// Shared TabsTrigger className matching PARSystemsPage style
const TAB_TRIGGER_CLASS =
  "rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:shadow-none px-4 py-2.5 text-sm gap-2 text-muted-foreground data-[state=active]:text-foreground data-[state=active]:font-medium whitespace-nowrap";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NegotiationsTabProps {
  /** All PAR invoice rows — fed into suggestion engine and case builder. */
  allInvoiceRows: PARInvoiceRow[];
  /** Vendor summaries computed from allInvoiceRows. */
  vendors: VendorSummary[];
  /** True while the parent is still fetching PAR invoice data. Drives loading skeletons. */
  loading?: boolean;
  /** Called when user clicks "View vendor" — navigates to vendor-detail tab. */
  onViewVendor?: (vendorName: string) => void;
  /**
   * When a thread id is deep-linked from outside (e.g. ?thread=…), the parent
   * can pass it in. The component will default to the Active Negotiations tab
   * and pre-select the thread.
   */
  initialThreadId?: string | null;
  /**
   * Override the initial primary tab. Defaults to "vendors" unless
   * initialThreadId is set (in which case it defaults to "active").
   * Useful for testing and for parent-driven navigation.
   */
  initialTab?: PrimaryTab;
}

// ---------------------------------------------------------------------------
// Helper: build dialog state for a given vendor name
// ---------------------------------------------------------------------------

interface DialogStateForVendor {
  vendorName: string;
  negCase: NegotiationCase | null;
  suggestedTerms: SuggestedTerms | null;
  defaultEmail: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NegotiationsTab({
  allInvoiceRows,
  vendors,
  loading = false,
  onViewVendor,
  initialThreadId,
  initialTab,
}: NegotiationsTabProps) {
  const {
    threads,
    isLoading: threadsLoading,
    createThread,
    isCreating,
    refetch: refetchThreads,
  } = useNegotiations();
  const { contacts, isLoading: contactsLoading } = useVendorContacts();
  const { getTermsForVendor, isLoading: termsLoading } = useVendorTermsLookup();
  const { settings } = useSettings();
  const { config: signalConfig, update: updateSignalConfig } = useNegotiationConfig();
  const { campaigns } = useNegotiationCampaigns();
  const { labelsByVendor } = useVendorLabels();

  // All vendor terms as array for analytics
  const vendorTermsAll = useMemo(() => {
    return vendors.map((v) => getTermsForVendor(v.vendor_name)).filter(Boolean) as NonNullable<
      ReturnType<typeof getTermsForVendor>
    >[];
  }, [vendors, getTermsForVendor]);

  // ── Exclusion set for suggestions ──
  const excludeSet = useMemo(() => {
    const names = new Set<string>();
    // Active/needs_attention threads
    for (const t of threads) {
      if ((t.status === "active" || t.status === "needs_attention") && t.vendor_name) {
        names.add(t.vendor_name);
      }
    }
    // Agreed vendor_terms
    for (const vt of vendorTermsAll) {
      if (vt.supplier_name) names.add(vt.supplier_name);
    }
    // Excluded contacts
    for (const c of contacts) {
      if (c.excluded) names.add(c.vendor_name);
    }
    return names;
  }, [threads, vendorTermsAll, contacts]);

  // ── Suggestions (used for campaign builder defaults) ──
  const suggestions = useMemo(
    () =>
      buildNegotiationSuggestions(vendors, allInvoiceRows, {
        excludeVendorNames: excludeSet,
        config: signalConfig,
      }),
    [vendors, allInvoiceRows, excludeSet, signalConfig]
  );

  // ── Roster (unified — all vendors with enriched fields) ──
  const roster = useMemo(
    () =>
      buildVendorRoster({
        vendors,
        threads,
        vendorTerms: vendorTermsAll,
        contacts,
        suggestions,
        allInvoiceRows,
        signalConfig,
        campaigns,
      }),
    [
      vendors,
      threads,
      vendorTermsAll,
      contacts,
      suggestions,
      allInvoiceRows,
      signalConfig,
      campaigns,
    ]
  );

  // ── Cost of capital for term suggestions ──
  const costOfCapitalApr = (settings as any)?.cost_of_capital_apr ?? 12;
  const defaultTone = (settings as any)?.default_negotiation_tone ?? null;

  // ── Sorted, deduped vendor name list for the dialog picker ──
  const vendorOptions = useMemo(() => {
    const names = Array.from(new Set(vendors.map((v) => v.vendor_name))).sort();
    return names;
  }, [vendors]);

  // ── URL search params for state persistence across refresh ──
  //
  // NOTE: PARSystemsPage writes the outer ?tab= param via window.history.replaceState
  // (not React Router's setSearchParams), so React Router's internal location state
  // lags behind. To avoid stomping on ?tab=negotiations when we update inner params,
  // we always read from window.location.search (the real URL) rather than React
  // Router's `prev` snapshot.
  const [searchParams] = useSearchParams();

  /**
   * Read a param from the real URL (window.location.search).
   * Falls back to React Router's searchParams when window is unavailable (tests).
   */
  function getParam(key: string): string | null {
    try {
      return new URLSearchParams(window.location.search).get(key);
    } catch {
      return searchParams.get(key);
    }
  }

  /**
   * Resolve the initial primary tab from (in priority order):
   *  1. Explicit prop (useful for tests / parent-driven nav)
   *  2. URL ?ntab= (explicit tab persistence)
   *  3. URL ?nsub= presence (implies "active" tab)
   *  4. URL ?nthread= presence (implies "active" tab)
   *  5. initialThreadId prop (implies "active" tab)
   *  6. Default: "vendors"
   */
  function resolveInitialPrimaryTab(): PrimaryTab {
    if (initialTab) return initialTab;
    // Use React Router's params for initial resolution (they match window on mount)
    const ntabParam = searchParams.get("ntab");
    if (ntabParam === "active" || ntabParam === "vendors") return ntabParam;
    const nsubParam = searchParams.get("nsub");
    const nthreadParam = searchParams.get("nthread");
    if (nsubParam || nthreadParam || initialThreadId) return "active";
    return "vendors";
  }

  function resolveInitialSubPanel(): SubPanel {
    const nsubParam = searchParams.get("nsub");
    if (nsubParam === "templates") return "templates";
    return "threads";
  }

  function resolveInitialThreadId(): string | null {
    const nthreadParam = searchParams.get("nthread");
    return nthreadParam ?? initialThreadId ?? null;
  }

  // ── Active thread / panel state (initialized from URL / props) ──
  const [activeThreadId, setActiveThreadId] = useState<string | null>(resolveInitialThreadId);
  const [primaryTab, setPrimaryTab] = useState<PrimaryTab>(resolveInitialPrimaryTab);
  const [subPanel, setSubPanel] = useState<SubPanel>(resolveInitialSubPanel);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Track previous values so we can decide push vs replace
  const prevPrimaryTabRef = useRef<PrimaryTab | null>(null);
  const prevActiveThreadIdRef = useRef<string | null | undefined>(undefined);

  // ── Sync state → URL ──
  //
  // Strategy:
  //   • Switching the primary tab (vendors ↔ active) pushes a history entry so
  //     Back steps through it.
  //   • Selecting a thread pushes a history entry so Back closes it.
  //   • Deselecting a thread (clearing) also pushes so Back restores the thread.
  //   • All other updates (sub-panel toggle, first-render) use replaceState.
  //
  // We write directly to window.history rather than using setSearchParams so we
  // preserve the outer ?tab=negotiations param written by PARSystemsPage (which
  // also uses window.history directly and is invisible to React Router's location).
  useEffect(() => {
    // Build new params on top of the REAL current URL (not React Router's stale snapshot)
    const realParams = new URLSearchParams(window.location.search);
    realParams.set("ntab", primaryTab);
    if (primaryTab === "active") {
      realParams.set("nsub", subPanel);
      if (activeThreadId) {
        realParams.set("nthread", activeThreadId);
      } else {
        realParams.delete("nthread");
      }
    } else {
      realParams.delete("nsub");
      realParams.delete("nthread");
    }

    const newUrl = `?${realParams.toString()}`;

    // Determine whether this change warrants a new history entry:
    //  - primary tab actually changed (not initial mount)
    //  - thread id changed (selected or deselected)
    const tabChanged =
      prevPrimaryTabRef.current !== null && prevPrimaryTabRef.current !== primaryTab;
    const threadChanged =
      prevActiveThreadIdRef.current !== undefined &&
      prevActiveThreadIdRef.current !== activeThreadId;
    const shouldPush = tabChanged || threadChanged;

    if (shouldPush) {
      window.history.pushState({}, "", newUrl);
    } else {
      window.history.replaceState({}, "", newUrl);
    }

    prevPrimaryTabRef.current = primaryTab;
    prevActiveThreadIdRef.current = activeThreadId;
  }, [primaryTab, subPanel, activeThreadId]);

  // ── Listen for Back/Forward button presses and restore state from URL ──
  useEffect(() => {
    function handlePopState() {
      const p = new URLSearchParams(window.location.search);
      const ntab = p.get("ntab");
      if (ntab === "active" || ntab === "vendors") {
        // Use functional updates to avoid stale closure warnings
        setPrimaryTab(ntab as PrimaryTab);
      }
      const nsub = p.get("nsub");
      if (nsub === "templates") {
        setSubPanel("templates");
      } else if (nsub === "threads" || ntab === "active") {
        setSubPanel("threads");
      }
      const nthread = p.get("nthread");
      setActiveThreadId(nthread ?? null);
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // ── Start-negotiation state (for prefilling dialog) ──
  const [dialogVendorName, setDialogVendorName] = useState<string | null>(null);
  const [dialogNegCase, setDialogNegCase] = useState<NegotiationCase | null>(null);
  const [dialogSuggestedTerms, setDialogSuggestedTerms] = useState<SuggestedTerms | null>(null);
  const [dialogDefaultEmail, setDialogDefaultEmail] = useState<string | null>(null);
  const [dialogCurrentTerms, setDialogCurrentTerms] = useState<{
    discount_pct: number | null;
    days_early: number | null;
    payment_terms?: string | null;
    mixed_terms?: string[] | null;
  } | null>(null);
  const [dialogCooldownError, setDialogCooldownError] = useState<CooldownError | null>(null);
  const [cooldownOverride, setCooldownOverride] = useState(false);

  /**
   * Build and apply dialog state for a given vendor name.
   * Called both when opening the dialog for a specific vendor and when the
   * user picks a vendor inside the generic dialog (onVendorSelect).
   */
  function applyVendorToDialog(vendorName: string): DialogStateForVendor {
    const vendorSummary = vendors.find(
      (v) => v.vendor_name.toLowerCase() === vendorName.toLowerCase()
    );
    const vendorTermsRow = getTermsForVendor(vendorName);
    const contact = contacts.find((c) => c.vendor_name.toLowerCase() === vendorName.toLowerCase());

    let negCase: NegotiationCase | null = null;
    let suggested: SuggestedTerms | null = null;

    if (vendorSummary) {
      negCase = buildNegotiationCase(
        vendorSummary,
        allInvoiceRows.filter((r) => r.vendor_name === vendorSummary.vendor_name),
        vendorTermsRow
      );
      suggested = suggestTerms(vendorSummary, allInvoiceRows, costOfCapitalApr);
    }

    const defaultEmail = contact?.supplier_email ?? null;

    // Derive current terms (discount/days from agreed vendor_terms; net/mixed from PAR summary)
    let currentTerms: {
      discount_pct: number | null;
      days_early: number | null;
      payment_terms?: string | null;
      mixed_terms?: string[] | null;
    } | null = null;

    // Derive net/mixed terms from vendorSummary.terms_used
    const termsUsed = vendorSummary?.terms_used ?? [];
    const uniqueTerms = Array.from(new Set(termsUsed.filter(Boolean)));
    const paymentTerms = uniqueTerms.length === 1 ? uniqueTerms[0] : null;
    const mixedTerms = uniqueTerms.length > 1 ? uniqueTerms : null;

    if (vendorTermsRow) {
      const agreedDays = vendorTermsRow.agreed_days_early ?? null;
      const agreedApr = vendorTermsRow.agreed_apr ?? null;
      const agreedDiscount =
        agreedApr != null && agreedDays != null && agreedDays > 0
          ? (agreedApr * agreedDays) / 360
          : null;
      currentTerms = {
        discount_pct: agreedDiscount,
        days_early: agreedDays,
        payment_terms: paymentTerms,
        mixed_terms: mixedTerms,
      };
    } else if (vendorSummary?.best_discount_pct != null || uniqueTerms.length > 0) {
      currentTerms = {
        discount_pct: vendorSummary?.best_discount_pct ?? null,
        days_early: null,
        payment_terms: paymentTerms,
        mixed_terms: mixedTerms,
      };
    }

    setDialogVendorName(vendorName);
    setDialogNegCase(negCase);
    setDialogSuggestedTerms(suggested);
    setDialogDefaultEmail(defaultEmail);
    setDialogCurrentTerms(currentTerms);

    return { vendorName, negCase, suggestedTerms: suggested, defaultEmail };
  }

  function openStartDialog(vendorName: string) {
    setDialogCooldownError(null);
    setCooldownOverride(false);

    if (vendorName) {
      applyVendorToDialog(vendorName);
    } else {
      setDialogVendorName(null);
      setDialogNegCase(null);
      setDialogSuggestedTerms(null);
      setDialogDefaultEmail(null);
      setDialogCurrentTerms(null);
    }

    setDialogOpen(true);
  }

  /**
   * Called from inside NewNegotiationDialog when the user selects a vendor
   * via the vendor picker (no-preselected-vendor flow).
   */
  function handleVendorSelect(vendorName: string) {
    applyVendorToDialog(vendorName);
  }

  async function handleCreate(data: NewNegotiationSubmitPayload) {
    try {
      const result = await createThread({
        ...data,
        vendor_name: data.vendor_name ?? dialogVendorName,
        recipient_emails: data.recipients.slice(1),
        overrideCooldown: cooldownOverride,
      });
      setActiveThreadId(result.thread.id);
      setPrimaryTab("active");
      setSubPanel("threads");
    } catch (err) {
      if (err instanceof CooldownError) {
        setDialogCooldownError(err);
        throw err;
      }
      throw err;
    }
  }

  // Build vendor context for the active thread's vendor
  const activeThreadVendorContext = useMemo(() => {
    if (!activeThreadId) return null;
    const thread = threads.find((t) => t.id === activeThreadId);
    const vendorName = thread?.vendor_name;
    if (!vendorName) return null;
    const vendorSummary = vendors.find(
      (v) => v.vendor_name.toLowerCase() === vendorName.toLowerCase()
    );
    if (!vendorSummary) return null;
    const vendorTermsRow = getTermsForVendor(vendorName);
    return buildNegotiationCase(
      vendorSummary,
      allInvoiceRows.filter((r) => r.vendor_name === vendorSummary.vendor_name),
      vendorTermsRow
    );
  }, [activeThreadId, threads, vendors, allInvoiceRows, getTermsForVendor]);

  const handleSelectThread = (id: string) => {
    setActiveThreadId(id);
    setPrimaryTab("active");
    setSubPanel("threads");
  };

  const needsAttentionCount = threads.filter((t) => t.status === "needs_attention").length;

  // While any underlying source is still loading, show a skeleton.
  const isLoading = loading || threadsLoading || termsLoading || contactsLoading;

  return (
    <div className="space-y-6">
      {/* ── Primary two-tab layout ── */}
      <Tabs
        value={primaryTab}
        onValueChange={(v) => setPrimaryTab(v as PrimaryTab)}
        className="space-y-0"
      >
        <TabsList className="h-auto p-0 bg-transparent border-b border-border rounded-none w-full justify-start gap-0">
          {/* Vendors tab */}
          <TabsTrigger value="vendors" className={TAB_TRIGGER_CLASS}>
            <Users className="w-4 h-4" />
            Vendors
          </TabsTrigger>

          {/* Active Negotiations tab — shows needs-attention badge */}
          <TabsTrigger value="active" className={TAB_TRIGGER_CLASS}>
            <MessageSquare className="w-4 h-4" />
            Active Negotiations
            {needsAttentionCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold bg-warning text-warning-foreground px-1.5 min-w-[18px] h-[18px]">
                {needsAttentionCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Vendors tab content ── */}
        <TabsContent value="vendors" className="pt-6 space-y-4">
          {isLoading ? (
            <SuggestionsSkeleton rows={5} />
          ) : (
            <VendorsTable
              roster={roster}
              allVendorNames={vendorOptions}
              onVendorClick={onViewVendor}
              campaigns={campaigns}
              threads={threads}
              allInvoiceRows={allInvoiceRows}
              headerActions={
                <SignalConfigDialog config={signalConfig} onSave={updateSignalConfig} />
              }
            />
          )}
        </TabsContent>

        {/* ── Active Negotiations tab content ── */}
        <TabsContent value="active" className="pt-4 space-y-4">
          {/* Inner sub-nav (Threads | Templates) */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-0">
              {SUB_PANELS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSubPanel(p.id)}
                  className={`px-4 py-2 text-sm transition-colors border-b-2 ${
                    subPanel === p.id
                      ? "border-primary text-foreground font-medium bg-primary/5"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
                >
                  {p.label}
                  {p.id === "threads" && needsAttentionCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold bg-warning text-warning-foreground px-1.5 min-w-[18px] h-[18px]">
                      {needsAttentionCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {subPanel === "threads" && (
              <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => openStartDialog("")}>
                <MessageSquarePlus className="w-4 h-4" />
                New Negotiation
              </Button>
            )}
          </div>

          {/* ── Threads sub-panel ── */}
          {subPanel === "threads" && (
            <div className="flex gap-4 h-[calc(100vh-160px)] min-h-[400px]">
              {/* Thread list — bounded scroll box so a long list doesn't grow the page */}
              <div className="w-72 flex-shrink-0 border border-border rounded-lg overflow-y-auto h-full">
                {threadsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <ThreadList
                    threads={threads}
                    activeId={activeThreadId ?? undefined}
                    onSelect={handleSelectThread}
                    campaigns={campaigns}
                    labelsByVendor={labelsByVendor}
                  />
                )}
              </div>

              {/* Thread detail */}
              <div className="flex-1 min-w-0 h-full border border-border rounded-lg overflow-hidden">
                {activeThreadId ? (
                  <ThreadView
                    threadId={activeThreadId}
                    onBack={() => setActiveThreadId(null)}
                    onViewVendor={onViewVendor}
                    vendorContext={activeThreadVendorContext}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8 py-16">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                      <MessageSquare className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        Select a negotiation thread
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Choose a thread from the list, or start a new vendor negotiation.
                      </p>
                    </div>
                    <Button size="sm" onClick={() => openStartDialog("")}>
                      <MessageSquarePlus className="w-4 h-4 mr-2" />
                      Start a Negotiation
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Templates sub-panel ── */}
          {subPanel === "templates" && <TemplatesManager />}

          {/* ── Demo controls — only shown on Active Negotiations tab (flag-gated internally) ── */}
          <DemoControls
            threadId={activeThreadId ?? undefined}
            onSeedSuccess={refetchThreads}
            onSimulateSuccess={refetchThreads}
          />
        </TabsContent>
      </Tabs>

      {/* ── New Negotiation Dialog ── */}
      <NewNegotiationDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogCooldownError(null);
            setCooldownOverride(false);
          }
          setDialogOpen(open);
        }}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        defaultVendorName={dialogVendorName || undefined}
        defaultEmail={dialogDefaultEmail ?? undefined}
        defaultTone={defaultTone}
        negotiationCase={dialogNegCase}
        suggestedTerms={dialogSuggestedTerms}
        currentTerms={dialogCurrentTerms}
        vendorOptions={vendorOptions}
        onVendorSelect={handleVendorSelect}
      />

      {/* Cooldown override prompt */}
      {dialogCooldownError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full space-y-4 shadow-xl">
            <h4 className="text-sm font-semibold text-foreground">Decline cooldown active</h4>
            <p className="text-xs text-muted-foreground">{dialogCooldownError.message}</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDialogCooldownError(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setCooldownOverride(true);
                  setDialogCooldownError(null);
                  setDialogOpen(true);
                }}
              >
                Override & continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
